import apiService from './api';
import type {
  SprayApplication,
  SprayApplicationFormData,
  SprayWeatherLimits,
  WeatherLimitInput,
  WeatherLimitPreset,
  SignOffData,
  Chemical,
  BarcodeChemicalMatch,
  ChemicalDetail,
  ChemicalPackSize,
  ChemicalWithRegistration,
  ChemicalCropRegistration,
  ChemicalRegistrationRate,
  ChemicalStateRegistration,
  ChemicalHistoryEvent,
  ApvmaRegistrationsPreview,
  ChemicalInventoryLocation,
  InventoryStockLevel,
  InventoryTransaction,
  SprayOrgAdvisor,
  MyAdvisoryOrg,
  WaypointInput,
  WhpAssessment,
  SituationResolution,
  SituationCoverageEntry,
  SpraySituationDetail,
  LocationRecord,
  CoverageResult,
  ApiListResponse,
  ApiSingleResponse,
  StockCheckResult,
  SprayTankLoad,
  SprayTankMotion,
  TankMakeUp,
  StartTankInput,
  UpdateTankInput,
  SprayReconciliation,
  MixCheckResult,
  ChemicalIncompatibility,
  IncompatibilityInput,
  EquipmentCalibration,
  CalibrationInput,
  CalibrationPreview,
  LabelProposal,
  LabelApplyResult,
} from '../types/spray';
import { type Numeric } from '@/utils/numeric';

// ============================================================================
// AI APVMA LOOKUP TYPES
// ============================================================================
//
// Mirrors the JSON schema enforced by chemicalLookupService.js on the server.
// Kept inline here (not in types/spray.ts) because it's only used by the
// lookup flow.

export interface ChemicalLookupActiveIngredient {
  name: string;
  concentration_gl?: number | null;
  concentration_pct?: number | null;
  cas_number?: string | null;
}

export interface ChemicalLookupChemicalProposal {
  product_name?: string;
  trade_name?: string | null;
  manufacturer?: string | null;
  active_ingredients?: ChemicalLookupActiveIngredient[];
  formulation_type?: 'EC' | 'WP' | 'SC' | 'SL' | 'WG' | 'GR' | 'DF' | null;
  signal_word?: 'POISON' | 'DANGER' | 'WARNING' | 'CAUTION' | null;
  ppe_required?: string[];
  sds_url?: string | null;
  label_url?: string | null;
  apvma_approval_number?: string | null;
  mode_of_action_group?: string | null;
  mode_of_action_type?: string | null;
  confidence: 'HIGH' | 'MEDIUM';
  sources: string[];
}

export interface ChemicalLookupRegistrationProposal {
  situation_id: number;
  situation_name: string;
  registration_number?: string | null;
  target_pest: string;
  min_rate_per_ha?: number | null;
  max_rate_per_ha?: number | null;
  rate_unit: 'L/ha' | 'mL/ha' | 'kg/ha' | 'g/ha' | 'mL/100L' | 'g/100L';
  max_applications_per_season?: number | null;
  min_days_between_apps?: number | null;
  whp_days?: number | null;
  whp_export_days?: number | null;
  rei_hours?: number | null;
  growth_stage_restriction?: string | null;
  notes?: string | null;
  confidence: 'HIGH' | 'MEDIUM';
  source_url: string;
}

export interface ChemicalLookupSkippedItem {
  item: string;
  reason: string;
}

export interface ChemicalLookupProposal {
  chemical: ChemicalLookupChemicalProposal;
  registrations: ChemicalLookupRegistrationProposal[];
  skipped: ChemicalLookupSkippedItem[];
  notes_for_user?: string;
}

export interface ChemicalLookupResult {
  proposal: ChemicalLookupProposal;
  usage: { input_tokens: number; output_tokens: number; cache_read_input_tokens?: number };
  chemical: { id: number; product_name: string; trade_name: string | null };
  mode?: 'identity' | 'details';
}

export interface ChemicalDiscoverCandidate {
  product_name: string;
  manufacturer?: string | null;
  apvma_approval_number?: string | null;
  formulation_type?: string | null;
  active_constituents?: string | null;
  label_url?: string | null;
  source_url?: string | null;
}

export interface ChemicalDiscoverResult {
  candidates: ChemicalDiscoverCandidate[];
  notes_for_user: string;
  usage?: { input_tokens: number; output_tokens: number };
}

export interface ChemicalIdentityResult {
  proposal: ChemicalLookupProposal;
  usage?: { input_tokens: number; output_tokens: number };
}

export type LookupJobStatus = 'pending' | 'running' | 'done' | 'error';

export interface LookupJobStart {
  job_id: string;
}

export interface LookupJobState<T> {
  job_id: string;
  kind: 'discover' | 'identity' | 'lookup';
  status: LookupJobStatus;
  result: T | null;
  error: string | null;
}

export interface ChemicalDiscoverPayload {
  candidates: ChemicalDiscoverCandidate[];
  notes_for_user: string;
}

export interface ChemicalProposalPayload {
  proposal: ChemicalLookupProposal;
}

export interface ChemicalLookupApplyPayload {
  chemical?: Partial<ChemicalLookupChemicalProposal>;
  registrations?: ChemicalLookupRegistrationProposal[];
}

export interface ChemicalLookupApplyResult {
  chemicalUpdated: boolean;
  registrationsAdded: number;
  errors: { kind: 'chemical' | 'registration'; message: string }[];
  chemical: ChemicalDetail;
}

// ============================================================================
// ADVISOR CONTEXT
// ============================================================================

// When an advisor is acting on behalf of a different org, this header is sent
// with every spray API request. Set via setTargetOrg().
let _targetOrgId: number | null = null;

// Purchase orders send it too: an advisor raises them for the organisation
// they act for.
export function getAdvisorHeaders(): Record<string, string> {
  if (_targetOrgId !== null) {
    return { 'X-Target-Org-ID': String(_targetOrgId) };
  }
  return {};
}

// ============================================================================
// SPRAY SERVICE CLASS
// ============================================================================

class SprayService {

  /**
   * Set the target org for cross-org advisor access.
   * Pass null to revert to the user's own org.
   */
  setTargetOrg(orgId: number | null): void {
    _targetOrgId = orgId;
  }

  getTargetOrgId(): number | null {
    return _targetOrgId;
  }

  // ==========================================================================
  // SPRAY APPLICATIONS
  // ==========================================================================

  async getApplications(filters?: {
    status?: string;
    chemical_id?: number;
    date_from?: string;
    date_to?: string;
    operator_id?: number;
    /** Only sprays held over a withholding-period conflict — a Spray Admin queue. */
    whp_pending?: boolean;
    /** Nursery spraying lists only the sprays aimed at nursery stock. */
    kind?: 'nursery' | 'orchard';
    page?: number;
    limit?: number;
  }): Promise<ApiListResponse<SprayApplication>> {
    const params = new URLSearchParams();
    if (filters?.kind)         params.set('kind', filters.kind);
    if (filters?.status)       params.set('status', filters.status);
    if (filters?.chemical_id)  params.set('chemical_id', String(filters.chemical_id));
    if (filters?.date_from)    params.set('date_from', filters.date_from);
    if (filters?.date_to)      params.set('date_to', filters.date_to);
    if (filters?.operator_id)  params.set('operator_id', String(filters.operator_id));
    if (filters?.whp_pending)  params.set('whp_pending', 'true');
    if (filters?.page)         params.set('page', String(filters.page));
    if (filters?.limit)        params.set('limit', String(filters.limit));

    const query = params.toString() ? `?${params}` : '';
    return apiService.get(`/spray/applications${query}`, getAdvisorHeaders());
  }

  async getApplication(id: number): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.get(`/spray/applications/${id}`, getAdvisorHeaders());
  }

  /**
   * Dry-run the withholding period against the target blocks' harvest windows.
   * Saves nothing — the create runs the same check server-side regardless.
   */
  async checkWhp(data: {
    targets: { target_type: string; target_id: number }[];
    scheduled_date: string;
    chemicals?: { chemical_id: number; crop_registration_id?: number | null }[];
    chemical_id?: number;
    crop_registration_id?: number | null;
    whp_days?: number | null;
  }): Promise<ApiSingleResponse<WhpAssessment>> {
    return apiService.post('/spray/applications/whp-check', data, getAdvisorHeaders());
  }

  /**
   * Approve or reject a spray held over a withholding-period conflict.
   * Requires a Spray Admin seat — the server enforces it, the UI only hides
   * the buttons.
   */
  async decideWhpOverride(
    id: number,
    decision: 'approved' | 'rejected',
    notes?: string
  ): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post(
      `/spray/applications/${id}/whp-override`,
      { decision, notes: notes || null },
      getAdvisorHeaders()
    );
  }

  async createApplication(data: SprayApplicationFormData): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post('/spray/applications', data, getAdvisorHeaders());
  }

  async updateApplication(id: number, data: Partial<SprayApplicationFormData>): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.put(`/spray/applications/${id}`, data, getAdvisorHeaders());
  }

  async cancelApplication(id: number, reason?: string): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.delete(`/spray/applications/${id}`, { cancellation_reason: reason }, getAdvisorHeaders());
  }

  async assignOperator(id: number, operatorId: number, overrideReason?: string): Promise<ApiSingleResponse<SprayApplication>> {
    const body: Record<string, unknown> = { operator_id: operatorId };
    if (overrideReason) body.override_reason = overrideReason;
    return apiService.post(`/spray/applications/${id}/assign`, body, getAdvisorHeaders());
  }

  /**
   * `acknowledgeWeather` is the operator accepting a breach of the spray's
   * weather limits they have been shown. Without it a breach comes back as a
   * 409 WEATHER_LIMITS_BREACHED carrying the check (`details.weather_check`).
   */
  async startApplication(id: number, acknowledgeWeather = false): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post(
      `/spray/applications/${id}/start`,
      acknowledgeWeather ? { acknowledge_weather: true } : {},
      getAdvisorHeaders(),
    );
  }

  async pauseApplication(id: number): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post(`/spray/applications/${id}/pause`, {}, getAdvisorHeaders());
  }

  async resumeApplication(id: number): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post(`/spray/applications/${id}/resume`, {}, getAdvisorHeaders());
  }

  async completeApplication(id: number, data: SignOffData): Promise<ApiSingleResponse<SprayApplication>> {
    return apiService.post(`/spray/applications/${id}/complete`, data, getAdvisorHeaders());
  }

  // ==========================================================================
  // WAYPOINTS
  // ==========================================================================

  async postWaypoints(id: number, waypoints: WaypointInput[]): Promise<ApiSingleResponse<{ inserted: number }>> {
    return apiService.post(`/spray/applications/${id}/waypoints`, waypoints, getAdvisorHeaders());
  }

  async getWaypoints(id: number): Promise<ApiSingleResponse<LocationRecord[]>> {
    return apiService.get(`/spray/applications/${id}/waypoints`, getAdvisorHeaders());
  }

  async getCoverage(id: number): Promise<ApiSingleResponse<CoverageResult>> {
    return apiService.get(`/spray/applications/${id}/coverage`, getAdvisorHeaders());
  }

  // ==========================================================================
  // WEATHER LIMITS
  // ==========================================================================

  /** The operator's job sheet, as an A4 PDF. */
  async getJobSheetPdf(id: number): Promise<Blob> {
    return apiService.get(`/spray/applications/${id}/job-sheet.pdf`, {
      responseType: 'blob', headers: getAdvisorHeaders(),
    }) as unknown as Promise<Blob>;
  }

  async getWeatherLimits(id: number): Promise<ApiSingleResponse<SprayWeatherLimits>> {
    return apiService.get(`/spray/applications/${id}/weather-limits`, getAdvisorHeaders());
  }

  async saveWeatherLimits(id: number, limits: WeatherLimitInput[]): Promise<ApiSingleResponse<SprayWeatherLimits>> {
    return apiService.put(`/spray/applications/${id}/weather-limits`, { limits }, getAdvisorHeaders());
  }

  async getWeatherLimitPresets(): Promise<ApiSingleResponse<WeatherLimitPreset[]>> {
    return apiService.get('/spray/weather-limit-presets', getAdvisorHeaders());
  }

  async createWeatherLimitPreset(
    data: { name: string; description?: string | null; limits: WeatherLimitInput[] },
  ): Promise<ApiSingleResponse<WeatherLimitPreset>> {
    return apiService.post('/spray/weather-limit-presets', data, getAdvisorHeaders());
  }

  async updateWeatherLimitPreset(
    presetId: number,
    data: { name: string; description?: string | null; limits: WeatherLimitInput[] },
  ): Promise<ApiSingleResponse<WeatherLimitPreset>> {
    return apiService.put(`/spray/weather-limit-presets/${presetId}`, data, getAdvisorHeaders());
  }

  async deleteWeatherLimitPreset(presetId: number): Promise<ApiSingleResponse<{ deleted: boolean }>> {
    return apiService.delete(`/spray/weather-limit-presets/${presetId}`, undefined, getAdvisorHeaders());
  }

  async getCurrentWeather(id: number): Promise<ApiSingleResponse<{ source: string; snapshot: Record<string, unknown> } | null>> {
    return apiService.get(`/spray/applications/${id}/current-weather`, getAdvisorHeaders());
  }

  // ==========================================================================
  // CHEMICALS
  // ==========================================================================

  async searchChemicals(query = '', page = 1, limit = 20): Promise<ApiListResponse<Chemical>> {
    return apiService.get(`/spray/chemicals?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`);
  }

  async getChemicalByBarcode(barcode: string): Promise<ApiListResponse<BarcodeChemicalMatch>> {
    return apiService.get(`/spray/chemicals?barcode=${encodeURIComponent(barcode)}`);
  }

  async getChemicalsBySituation(situationId: number): Promise<ApiSingleResponse<ChemicalWithRegistration[]>> {
    return apiService.get(`/spray/chemicals/by-situation/${situationId}`);
  }

  async getSituations(): Promise<ApiSingleResponse<{ id: number; name: string; description: string | null; is_active: boolean }[]>> {
    return apiService.get('/spray/situations');
  }

  /** Every situation, inactive included, with its grouping and usage counts. */
  async getSituationsDetail(): Promise<ApiSingleResponse<SpraySituationDetail[]>> {
    return apiService.get('/spray/situations?all=true');
  }

  /** What a planting sprayed as this situation is searched against. */
  async getSituationCoverage(id: number): Promise<ApiSingleResponse<SituationCoverageEntry[]>> {
    return apiService.get(`/spray/situations/${id}/coverage`);
  }

  /**
   * Place a situation in the grouping (Spray Admin). Global — it changes
   * every org's chemical lists. Setting `same_as_id` clears `parent_id`.
   */
  async setSituationGroup(
    id: number,
    data: { same_as_id?: number | null; parent_id?: number | null; caveat?: string | null },
  ): Promise<ApiSingleResponse<{ id: number; same_as_id: number | null; parent_id: number | null }>> {
    return apiService.put(`/spray/situations/${id}/group`, data);
  }

  /** Work out the spray situation from the targets' cultivars. Saves nothing. */
  async resolveSituations(
    targets: { target_type: string; target_id: number; row_number?: number | null; row_side?: string | null }[],
  ): Promise<ApiSingleResponse<SituationResolution>> {
    return apiService.post('/spray/applications/situation-resolve', { targets }, getAdvisorHeaders());
  }

  /** Link a crop type to its spray situation (Spray Admin). Global. */
  async setCropTypeSituation(
    cropTypeId: number,
    spraySituationId: number | null,
  ): Promise<ApiSingleResponse<{ id: number; name: string; spray_situation_id: number | null }>> {
    return apiService.put(`/spray/crop-types/${cropTypeId}/situation`, { spray_situation_id: spraySituationId });
  }

  /**
   * The label situation a crop is sprayed under as nursery stock (nursery
   * stock, ornamentals ...). Nursery sprays search it beside the crop's own.
   * Spray Admin. Global, like the crop's own link.
   */
  async setCropTypeNurserySituation(
    cropTypeId: number,
    spraySituationId: number | null,
  ): Promise<ApiSingleResponse<{ id: number; name: string; nursery_spray_situation_id: number | null }>> {
    return apiService.put(`/spray/crop-types/${cropTypeId}/nursery-situation`, { nursery_spray_situation_id: spraySituationId });
  }

  /**
   * Adds one of the organisation's own situations. A shared situation of the
   * same name is returned instead (`existing: true`).
   */
  async createSituation(name: string, description?: string): Promise<ApiSingleResponse<{ id: number; name: string; description: string | null; is_active: boolean; is_shared?: boolean; existing?: boolean }>> {
    return apiService.post('/spray/situations', { name, description });
  }

  async updateSituation(id: number, data: { name?: string; description?: string; is_active?: boolean }): Promise<ApiSingleResponse<{ id: number; name: string; description: string | null; is_active: boolean }>> {
    return apiService.put(`/spray/situations/${id}`, data);
  }

  async deleteSituation(id: number): Promise<ApiSingleResponse<{ id?: number; soft_deleted?: boolean }>> {
    return apiService.delete(`/spray/situations/${id}`);
  }

  async getChemical(id: number): Promise<ApiSingleResponse<ChemicalDetail>> {
    return apiService.get(`/spray/chemicals/${id}`);
  }

  async createChemical(data: Partial<Chemical>): Promise<ApiSingleResponse<Chemical>> {
    return apiService.post('/spray/chemicals', data, getAdvisorHeaders());
  }

  async updateChemical(id: number, data: Partial<Chemical>): Promise<ApiSingleResponse<Chemical>> {
    return apiService.put(`/spray/chemicals/${id}`, data, getAdvisorHeaders());
  }

  async deleteChemical(
    id: number,
  ): Promise<ApiSingleResponse<{ hardDeleted?: boolean; softDeleted?: boolean }>> {
    return apiService.delete(`/spray/chemicals/${id}`, undefined, getAdvisorHeaders());
  }

  async getRegistrations(chemicalId: number): Promise<ApiSingleResponse<ChemicalCropRegistration[]>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/registrations`);
  }

  async addRegistration(chemicalId: number, data: Partial<ChemicalCropRegistration>): Promise<ApiSingleResponse<ChemicalCropRegistration>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/registrations`, data, getAdvisorHeaders());
  }

  async updateRegistration(chemicalId: number, regId: number, data: Partial<ChemicalCropRegistration>): Promise<ApiSingleResponse<ChemicalCropRegistration>> {
    return apiService.put(`/spray/chemicals/${chemicalId}/registrations/${regId}`, data, getAdvisorHeaders());
  }

  async getStateRegistrations(chemicalId: number): Promise<ApiSingleResponse<ChemicalStateRegistration[]>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/state-registrations`);
  }

  /** Who changed this chemical and its registrations: the organisation's own record. */
  async getChemicalHistory(chemicalId: number): Promise<ApiSingleResponse<ChemicalHistoryEvent[]>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/history`, getAdvisorHeaders());
  }

  // Preview the product's registered crops/pests from APVMA PUBCRIS.
  async getApvmaRegistrations(chemicalId: number): Promise<ApiSingleResponse<ApvmaRegistrationsPreview>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/apvma-registrations`);
  }

  // Import the chosen crops' registered pests (rates blank).
  async importApvmaRegistrations(
    chemicalId: number,
    hostcodes: string[],
  ): Promise<ApiSingleResponse<{ added: number; skipped: number; situations: number }>> {
    return apiService.post(
      `/spray/chemicals/${chemicalId}/apvma-registrations/import`,
      { hostcodes },
      getAdvisorHeaders(),
    );
  }

  // Apply one rate/WHP set to many registrations at once.
  async bulkSetRegistrationRate(
    chemicalId: number,
    registrationIds: number[],
    fields: Partial<ChemicalCropRegistration>,
  ): Promise<ApiSingleResponse<{ updated: number }>> {
    return apiService.patch(
      `/spray/chemicals/${chemicalId}/registrations/bulk-rate`,
      { registration_ids: registrationIds, ...fields },
      getAdvisorHeaders(),
    );
  }

  // ==========================================================================
  // APVMA LABEL EXTRACTION
  // ==========================================================================

  // Read the product's approved label and propose rates / WHPs / metadata.
  // Nothing is written; this is the review step. Slower than the PUBCRIS calls
  // (it fetches and parses a PDF), so expect a few seconds.
  async readApvmaLabel(chemicalId: number): Promise<ApiSingleResponse<LabelProposal>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/label/read`, {}, getAdvisorHeaders());
  }

  // Persist the rows a reviewer accepted. Requires a spray admin seat — the
  // rows carry withholding periods that gate spray creation.
  async applyApvmaLabel(
    chemicalId: number,
    payload: {
      label_url?: string;
      chemical?: LabelProposal['chemical'];
      rows: Array<{
        registration_id: number;
        page?: number | null;
        min_rate_per_ha?: number | null;
        max_rate_per_ha?: number | null;
        rate_unit?: string;
        whp_days?: number | null;
        whp_export_days?: number | null;
        rei_hours?: number | null;
        notes?: string | null;
      }>;
    },
  ): Promise<ApiSingleResponse<LabelApplyResult>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/label/apply`, payload, getAdvisorHeaders());
  }

  // Promote machine-read rates to 'entered' once checked against the label.
  async confirmLabelRates(
    chemicalId: number,
    registrationIds: number[],
  ): Promise<ApiSingleResponse<{ confirmed: number }>> {
    return apiService.post(
      `/spray/chemicals/${chemicalId}/label/confirm`,
      { registration_ids: registrationIds },
      getAdvisorHeaders(),
    );
  }

  // ==========================================================================
  // AI APVMA LOOKUP
  // ==========================================================================

  // Deterministic APVMA PUBCRIS lookups — synchronous, sub-second. No AI,
  // no jobs, no polling.

  // Type a chemical name / active ingredient -> matching registered AU
  // products from the local APVMA mirror.
  async searchApvma(query: string): Promise<ApiSingleResponse<ChemicalDiscoverPayload>> {
    return apiService.post('/spray/chemicals/lookup/search', { query }, getAdvisorHeaders());
  }

  // Build the full identity from the picked product's APVMA number.
  async lookupCandidateIdentity(
    candidate: ChemicalDiscoverCandidate,
  ): Promise<ApiSingleResponse<ChemicalProposalPayload>> {
    return apiService.post('/spray/chemicals/lookup/identity', { candidate }, getAdvisorHeaders());
  }

  // Re-build identity for an existing record from its stored APVMA number.
  async lookupChemicalIdentity(
    chemicalId: number,
  ): Promise<ApiSingleResponse<ChemicalProposalPayload>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/lookup/identity`, {}, getAdvisorHeaders());
  }

  async applyChemicalLookup(
    chemicalId: number,
    payload: ChemicalLookupApplyPayload,
  ): Promise<ApiSingleResponse<ChemicalLookupApplyResult>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/lookup/apply`, payload, getAdvisorHeaders());
  }

  // ==========================================================================
  // PACK SIZES
  // ==========================================================================

  async getPackSizes(chemicalId: number): Promise<ApiSingleResponse<ChemicalPackSize[]>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/pack-sizes`);
  }

  async createPackSize(chemicalId: number, data: Partial<ChemicalPackSize>): Promise<ApiSingleResponse<ChemicalPackSize>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/pack-sizes`, data, getAdvisorHeaders());
  }

  async updatePackSize(chemicalId: number, packId: number, data: Partial<ChemicalPackSize>): Promise<ApiSingleResponse<ChemicalPackSize>> {
    return apiService.put(`/spray/chemicals/${chemicalId}/pack-sizes/${packId}`, data, getAdvisorHeaders());
  }

  async deletePackSize(chemicalId: number, packId: number): Promise<{ success: boolean }> {
    return apiService.delete(`/spray/chemicals/${chemicalId}/pack-sizes/${packId}`, undefined, getAdvisorHeaders());
  }

  // ==========================================================================
  // INVENTORY
  // ==========================================================================

  async getInventoryLocations(): Promise<ApiSingleResponse<ChemicalInventoryLocation[]>> {
    return apiService.get('/spray/inventory/locations', getAdvisorHeaders());
  }

  async createInventoryLocation(data: { name: string; description?: string; farm_id?: number | null }): Promise<ApiSingleResponse<ChemicalInventoryLocation>> {
    return apiService.post('/spray/inventory/locations', data, getAdvisorHeaders());
  }

  async updateInventoryLocation(id: number, data: Partial<ChemicalInventoryLocation>): Promise<ApiSingleResponse<ChemicalInventoryLocation>> {
    return apiService.put(`/spray/inventory/locations/${id}`, data, getAdvisorHeaders());
  }

  async getStockLevels(locationId?: number): Promise<ApiSingleResponse<InventoryStockLevel[]>> {
    const q = locationId ? `?location_id=${locationId}` : '';
    return apiService.get(`/spray/inventory${q}`, getAdvisorHeaders());
  }

  async getTransactionHistory(filters?: {
    location_id?: number;
    chemical_id?: number;
    type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiListResponse<InventoryTransaction>> {
    const params = new URLSearchParams();
    if (filters?.location_id) params.set('location_id', String(filters.location_id));
    if (filters?.chemical_id) params.set('chemical_id', String(filters.chemical_id));
    if (filters?.type)        params.set('type', filters.type);
    if (filters?.date_from)   params.set('date_from', filters.date_from);
    if (filters?.date_to)     params.set('date_to', filters.date_to);
    if (filters?.page)        params.set('page', String(filters.page));
    if (filters?.limit)       params.set('limit', String(filters.limit));
    const q = params.toString() ? `?${params}` : '';
    return apiService.get(`/spray/inventory/transactions${q}`, getAdvisorHeaders());
  }

  async recordPurchase(data: {
    location_id: number;
    chemical_id: number;
    quantity: number;
    unit?: string;
    supplier?: string;
    purchase_date?: string;
    batch_number?: string;
    expiry_date?: string;
    unit_cost?: number;
    notes?: string;
  }): Promise<ApiSingleResponse<InventoryTransaction>> {
    return apiService.post('/spray/inventory/transactions', data, getAdvisorHeaders());
  }

  /**
   * Record a stocktake correction. Submit the counted (absolute) quantity for a
   * chemical at a location; the server computes the signed delta against current
   * stock and stores it as an 'adjustment' transaction.
   */
  async recordAdjustment(data: {
    location_id: number;
    chemical_id: number;
    counted_quantity: number;
    unit?: string;
    notes?: string;
  }): Promise<ApiSingleResponse<{ transaction: InventoryTransaction | null; previousStock: number; countedQuantity: number; delta: number; noChange: boolean }>> {
    return apiService.post('/spray/inventory/adjustments', data, getAdvisorHeaders());
  }

  // ==========================================================================
  // ADVISORS
  // ==========================================================================

  /** Get orgs this user is an advisor for (no license required) */
  async getMyAdvisoryOrgs(): Promise<ApiSingleResponse<MyAdvisoryOrg[]>> {
    return apiService.get('/spray/advisors/my-access');
  }

  async getAdvisors(): Promise<ApiSingleResponse<SprayOrgAdvisor[]>> {
    return apiService.get('/spray/advisors', getAdvisorHeaders());
  }

  async grantAdvisorAccess(data: {
    advisor_user_id: number;
    advisor_org_id: number;
    permissions: string[];
    expires_at?: string | null;
    notes?: string;
  }): Promise<ApiSingleResponse<SprayOrgAdvisor>> {
    return apiService.post('/spray/advisors', data, getAdvisorHeaders());
  }

  async updateAdvisorAccess(id: number, data: Partial<SprayOrgAdvisor>): Promise<ApiSingleResponse<SprayOrgAdvisor>> {
    return apiService.put(`/spray/advisors/${id}`, data, getAdvisorHeaders());
  }

  async revokeAdvisorAccess(id: number): Promise<ApiSingleResponse<{ revoked: boolean }>> {
    return apiService.delete(`/spray/advisors/${id}`, {}, getAdvisorHeaders());
  }

  // ==========================================================================
  // LICENSED USERS
  // ==========================================================================

  /**
   * Read-only list of org members who currently hold a spray licence, plus
   * which licence type_keys each holds. Used by assignment pickers.
   *
   * Seat assignment is done via the Subscriptions UI, not here.
   */
  async getSprayLicensedUsers(): Promise<ApiSingleResponse<Array<{
    user_id: number;
    first_name: string | null;
    last_name: string | null;
    email: string;
    spray_licenses: string[];
  }>>> {
    return apiService.get('/spray/licensed-users', getAdvisorHeaders());
  }

  // ==========================================================================
  // CLIENT-SIDE CALCULATION HELPERS
  // ==========================================================================

  /**
   * Resolve the rate options to offer for a registration. Prefers the explicit
   * `rates` array; falls back to a single synthesized option built from the
   * mirrored min/max/rate_unit columns (registrations imported from APVMA or
   * filled via bulk-rate have no rate rows yet). Returns [] when no rate is set.
   */
  effectiveRates(reg: {
    rates?: ChemicalRegistrationRate[];
    min_rate_per_ha?: Numeric;
    max_rate_per_ha?: Numeric;
    rate_unit?: string | null;
  }): ChemicalRegistrationRate[] {
    if (reg.rates && reg.rates.length) return reg.rates;
    if (reg.min_rate_per_ha != null || reg.max_rate_per_ha != null) {
      return [{
        min_rate: reg.min_rate_per_ha ?? null,
        max_rate: reg.max_rate_per_ha ?? null,
        rate_unit: reg.rate_unit || 'L/ha',
        is_default: true,
        sort_order: 0,
      }];
    }
    return [];
  }

  /**
   * True when a rate unit is expressed per 100 L of spray mix (dilute rate)
   * rather than per hectare. Dilute rates need the spray water volume to
   * compute product quantity.
   */
  isDiluteRateUnit(unit?: string | null): boolean {
    return !!unit && /\/\s*100\s*L$/i.test(unit);
  }

  /**
   * Get the default coverage % for a row spacing pattern.
   */
  defaultCoverageForPattern(pattern: string): number | null {
    switch (pattern) {
      case 'every_row':         return 100;
      case 'every_second_row':  return 50;
      case 'every_third_row':   return 33.33;
      case 'custom':            return null;
      case 'weed_strip':        return null;
      default:                  return 100;
    }
  }

  /**
   * Calculate effective area and estimated chemical volume.
   */
  calculateVolumes(
    totalAreaHa: number,
    coveragePct: number,
    ratePerHa: number,
    concentrationFactor: number,
    tankSizeL = 1000,
    outputRateLpha?: number | null
  ) {
    const effectiveAreaHa = parseFloat(((totalAreaHa * coveragePct) / 100).toFixed(4));
    const estimatedChemicalL = parseFloat((effectiveAreaHa * ratePerHa * concentrationFactor).toFixed(2));

    return {
      effectiveAreaHa,
      estimatedChemicalL,
      tankMixes: this.tankFills(effectiveAreaHa, outputRateLpha, tankSizeL),
    };
  }

  /**
   * How many tank loads a job takes.
   *
   * Water volume ÷ tank capacity, rounded up — a job needing 2.1 tanks needs
   * three fills, not 2.1. The water volume comes from the sprayer's output
   * rate, which is the only thing that connects an area to a number of litres.
   *
   * Without an output rate this is genuinely unknowable and returns null; a 0
   * would read as "no tanks needed".
   *
   * (This replaces an earlier formula that divided the CHEMICAL volume by the
   * tank capacity. Product and water are different quantities — 2 L/ha over
   * 10 ha with a 1000 L tank came out as "0.02 tanks".)
   */
  tankFills(
    effectiveAreaHa: number,
    outputRateLpha?: number | null,
    tankSizeL?: number | null
  ): number | null {
    if (!outputRateLpha || outputRateLpha <= 0) return null;
    if (!tankSizeL || tankSizeL <= 0) return null;
    if (!effectiveAreaHa || effectiveAreaHa <= 0) return null;
    return Math.ceil((effectiveAreaHa * outputRateLpha) / tankSizeL);
  }

  /**
   * Rate-unit-aware product estimate. Per-hectare rates are dosed against area
   * (product = area × rate); per-100L (dilute) rates are dosed against the
   * spray-tank water volume (product = waterVolume ÷ 100 × rate), where the
   * water volume comes from the equipment's output rate (L/ha) × effective
   * area. Dilute rates therefore need `outputRateLpha` to be set — when it is
   * missing, `needsOutputRate` is true and the estimate is 0.
   *
   * `estimatedProduct` stays in the rate's own numeric unit (L / mL / kg / g),
   * matching calculateVolumes — no unit normalisation.
   */
  estimateSprayVolumes(opts: {
    totalAreaHa: number;
    coveragePct: number;
    rate: number;
    rateUnit?: string | null;
    concentrationFactor?: number;
    outputRateLpha?: number | null;
    tankSizeL?: number;
  }) {
    const {
      totalAreaHa, coveragePct, rate,
      rateUnit, concentrationFactor = 1, outputRateLpha, tankSizeL = 1000,
    } = opts;

    const effectiveAreaHa = parseFloat(((totalAreaHa * coveragePct) / 100).toFixed(4));
    const dilute = this.isDiluteRateUnit(rateUnit);
    const hasOutput = !!outputRateLpha && outputRateLpha > 0;
    const waterVolumeL = hasOutput ? parseFloat((effectiveAreaHa * (outputRateLpha as number)).toFixed(2)) : 0;

    let estimatedProduct = 0;
    let needsOutputRate = false;
    if (dilute) {
      if (hasOutput) {
        estimatedProduct = parseFloat(((waterVolumeL / 100) * rate * concentrationFactor).toFixed(2));
      } else {
        needsOutputRate = true;
      }
    } else {
      estimatedProduct = parseFloat((effectiveAreaHa * rate * concentrationFactor).toFixed(2));
    }

    // Loads of WATER, rounded up. See tankFills() — the previous formula here
    // divided the product volume by the tank capacity, which is a different
    // quantity entirely.
    const tankMixes = this.tankFills(effectiveAreaHa, outputRateLpha, tankSizeL);

    return { effectiveAreaHa, estimatedProduct, waterVolumeL, tankMixes, needsOutputRate, dilute };
  }

  // ==========================================================================
  // SPRAYER CALIBRATION
  //
  // A calibration is a measurement, not a typed-in number: the operator runs
  // the machine and records what it did, and the server derives the rate.
  // ==========================================================================

  /**
   * Compute a calibration WITHOUT storing it, so the resulting rate, hectares
   * per tank and minutes per tank are visible before anything is committed.
   */
  async previewCalibration(
    equipmentId: number, data: CalibrationInput
  ): Promise<ApiSingleResponse<CalibrationPreview>> {
    return apiService.post(`/spray/equipment/${equipmentId}/calibrations/preview`, data, getAdvisorHeaders());
  }

  /**
   * Record a calibration and make it the machine's current one. Passing
   * `spray_application_id` is also what clears that spray's calibration block.
   */
  async createCalibration(
    equipmentId: number, data: CalibrationInput
  ): Promise<ApiSingleResponse<EquipmentCalibration>> {
    return apiService.post(`/spray/equipment/${equipmentId}/calibrations`, data, getAdvisorHeaders());
  }

  /** History, newest first — what the machine was measured at, when, by whom. */
  async getCalibrations(equipmentId: number): Promise<ApiListResponse<EquipmentCalibration>> {
    return apiService.get(`/spray/equipment/${equipmentId}/calibrations`, getAdvisorHeaders());
  }

  /**
   * The current calibration with its age and staleness. `data` is null for a
   * machine never calibrated — an ordinary state, not an error.
   */
  async getLatestCalibration(equipmentId: number): Promise<ApiSingleResponse<EquipmentCalibration | null>> {
    return apiService.get(`/spray/equipment/${equipmentId}/calibrations/latest`, getAdvisorHeaders());
  }

  // ==========================================================================
  // TANK LOADS
  //
  // The operator's record of what actually went into each tank. Reads are open
  // to anyone with spray access — the tank log IS the compliance record, and a
  // manager has to be able to read it without taking the job off the operator.
  // ==========================================================================

  async getTankLoads(sprayId: number): Promise<ApiListResponse<SprayTankLoad>> {
    return apiService.get(`/spray/applications/${sprayId}/tanks`, getAdvisorHeaders());
  }

  /**
   * How each load was driven — speed, stops, implied flow, pass width — worked
   * out from the GPS track on the server. Diagnostic only; computed on read,
   * so it covers sprays recorded before it existed.
   */
  async getTankMotion(sprayId: number): Promise<ApiSingleResponse<SprayTankMotion>> {
    return apiService.get(`/spray/applications/${sprayId}/tanks/motion`, getAdvisorHeaders());
  }

  /** Water still needed to finish the spray, so the last tank is made up to it. */
  async getTankMakeUp(sprayId: number): Promise<ApiSingleResponse<TankMakeUp>> {
    return apiService.get(`/spray/applications/${sprayId}/tanks/make-up`, getAdvisorHeaders());
  }

  /**
   * Start the next tank. Closes whichever load is currently open — in the
   * field the only signal that a tank is finished is the next one being mixed.
   *
   * With an empty body the load takes the sprayer's capacity and every product
   * is pre-filled with what the plan expects for that volume.
   */
  async startTankLoad(sprayId: number, data: StartTankInput = {}): Promise<ApiSingleResponse<SprayTankLoad>> {
    return apiService.post(`/spray/applications/${sprayId}/tanks`, data, getAdvisorHeaders());
  }

  /** Close the open load without starting another — that was the last tank. */
  async finishTankLoad(sprayId: number): Promise<ApiSingleResponse<SprayTankLoad>> {
    return apiService.post(`/spray/applications/${sprayId}/tanks/finish`, {}, getAdvisorHeaders());
  }

  async updateTankLoad(
    sprayId: number, tankId: number, data: UpdateTankInput
  ): Promise<ApiSingleResponse<SprayTankLoad>> {
    return apiService.put(`/spray/applications/${sprayId}/tanks/${tankId}`, data, getAdvisorHeaders());
  }

  async deleteTankLoad(sprayId: number, tankId: number): Promise<ApiSingleResponse<{ deleted: boolean }>> {
    return apiService.delete(`/spray/applications/${sprayId}/tanks/${tankId}`, getAdvisorHeaders());
  }

  /**
   * What the numbers say right now, without storing anything — lets an
   * operator see the applied rate and the label verdict before committing to
   * sign-off.
   */
  async getReconciliation(sprayId: number): Promise<ApiSingleResponse<SprayReconciliation>> {
    return apiService.get(`/spray/applications/${sprayId}/reconciliation`, getAdvisorHeaders());
  }

  /**
   * Recompute and STORE the reconciliation. Completion does this
   * automatically; this is for when a tank record is corrected afterwards.
   */
  async reconcile(sprayId: number, actualAreaHa?: number | null): Promise<ApiSingleResponse<SprayReconciliation>> {
    return apiService.post(
      `/spray/applications/${sprayId}/reconcile`,
      { actual_area_ha: actualAreaHa ?? null },
      getAdvisorHeaders()
    );
  }

  // ==========================================================================
  // TANK-MIX COMPATIBILITY
  // ==========================================================================

  /**
   * Check a set of products against each other and get the fill order back.
   *
   * Side-effect free, so it is safe to call on every change to the mix. A
   * verdict of 'ok' means nothing is recorded against the mix — never that it
   * has been certified safe.
   */
  async checkCompatibility(chemicalIds: number[]): Promise<ApiSingleResponse<MixCheckResult>> {
    return apiService.post('/spray/compatibility/check', { chemical_ids: chemicalIds }, getAdvisorHeaders());
  }

  async getIncompatibilities(chemicalId: number): Promise<ApiListResponse<ChemicalIncompatibility>> {
    return apiService.get(`/spray/chemicals/${chemicalId}/incompatibilities`, getAdvisorHeaders());
  }

  async createIncompatibility(
    chemicalId: number, data: IncompatibilityInput
  ): Promise<ApiSingleResponse<ChemicalIncompatibility>> {
    return apiService.post(`/spray/chemicals/${chemicalId}/incompatibilities`, data, getAdvisorHeaders());
  }

  async deleteIncompatibility(ruleId: number): Promise<ApiSingleResponse<{ deleted: boolean }>> {
    return apiService.delete(`/spray/chemicals/incompatibilities/${ruleId}`, getAdvisorHeaders());
  }

  /**
   * Calculate earliest safe harvest date given scheduled_date and whp_days.
   */
  earliestHarvestDate(scheduledDate: string, whpDays: number | null | undefined): string | null {
    if (!whpDays || whpDays <= 0) return null;
    const d = new Date(scheduledDate);
    d.setDate(d.getDate() + whpDays);
    return d.toISOString().split('T')[0];
  }

  /**
   * Pre-flight stock check before creating a spray.
   * Returns sufficiency result so the UI can warn user or prompt a PO.
   */
  async checkStock(data: {
    chemical_id: number;
    inventory_location_id: number;
    required_quantity: number;
    unit: string;
  }): Promise<ApiSingleResponse<StockCheckResult>> {
    return apiService.post('/spray/applications/check-stock', data, getAdvisorHeaders());
  }
}

export default new SprayService();
